import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type PaymentAccount = Database['public']['Tables']['payment_accounts']['Row'];

interface UsePaymentAccountInfoReturn {
  getAccountInfo: (accountId: string | number | null | undefined) => PaymentAccount | null;
  loading: boolean;
}

export const usePaymentAccountInfo = (): UsePaymentAccountInfoReturn => {
  const [accounts, setAccounts] = useState<PaymentAccount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
          .from('payment_accounts')
          .select('*')
          .eq('user_id', user.id);

        if (error) throw error;
        setAccounts(data || []);
      } catch (error) {
        console.error('Erro ao buscar contas de pagamento:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();

    // Configurar subscription para atualizações em tempo real
    const channelName = `payment_accounts_info_changes_${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payment_accounts'
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setAccounts(prev => [payload.new as PaymentAccount, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            setAccounts(prev =>
              prev.map(account =>
                account.id === payload.new.id ? payload.new as PaymentAccount : account
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setAccounts(prev =>
              prev.filter(account => account.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const getAccountInfo = (accountId: string | number | null | undefined): PaymentAccount | null => {
    if (!accountId) return null;

    const normalizedId = typeof accountId === 'number'
      ? accountId.toString()
      : accountId;

    return accounts.find(account => account.id?.toString() === normalizedId) || null;
  };

  return {
    getAccountInfo,
    loading
  };
};
